"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, PresenceContext, useMotionValue, type Variants } from "motion/react";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import { durations, easings, presets, springs, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { cn } from "@/components/wingo/lib/utils";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { inertProps } from "@/components/wingo/lib/with-ref";

export type AccordionVariant = "ghost" | "outline" | "soft" | "separated";
export type AccordionSize = "sm" | "md" | "lg";
export type AccordionRadius = "none" | "sm" | "md" | "lg" | "full";
export type AccordionIndicator = "chevron" | "plus" | "none";
export type AccordionIndicatorPosition = "start" | "end";
export type AccordionHeadingLevel = 2 | 3 | 4 | 5 | 6;
export type AccordionType = "single" | "multiple";
export type AccordionSlot = "item" | "trigger" | "icon" | "title" | "description" | "extra" | "indicator" | "content";
export type { Tone as AccordionTone };

export type AccordionItemData = {
  // unique within one accordion: it is what value / defaultValue / onValueChange speak
  value: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  // a short value at the end of the row, before the indicator: a price, a count, a status (collapsible's extra)
  extra?: ReactNode;
  content: ReactNode;
  disabled?: boolean;
};

// surfaces with a radius prop (outline and soft containers, separated cards)
const SURFACE_RADII: Record<AccordionRadius, string> = {
  none: "rounded-none",
  sm: "rounded-[12px]",
  md: "rounded-2xl",
  lg: "rounded-3xl",
  full: "rounded-3xl",
};

// the button's control radii: the ghost row has no surface, so the radius shapes its focus ring
const RADII: Record<AccordionRadius, Record<AccordionSize, string>> = {
  none: { sm: "rounded-none", md: "rounded-none", lg: "rounded-none" },
  sm: { sm: "rounded-[6px]", md: "rounded-[8px]", lg: "rounded-[10px]" },
  md: { sm: "rounded-[8px]", md: "rounded-[10px]", lg: "rounded-[12px]" },
  lg: { sm: "rounded-[10px]", md: "rounded-[14px]", lg: "rounded-[16px]" },
  full: { sm: "rounded-full", md: "rounded-full", lg: "rounded-full" },
};

// row 44 / 56 / 64px, title 14 / 15 / 16px, content 13 / 14 / 15px. line and tileLine are the height of the first
// row (the title's line, or the icon tile) so the indicator and the extra value stay on it when the title wraps
const SIZES: Record<
  AccordionSize,
  Record<
    | "vars"
    | "row"
    | "title"
    | "description"
    | "extra"
    | "content"
    | "tile"
    | "textMin"
    | "line"
    | "tileLine"
    | "indicator",
    string
  >
> = {
  sm: {
    vars: "[--accordion-tile:28px] [--accordion-indicator:24px]",
    row: "min-h-11 py-2.5",
    title: "text-sm/5",
    description: "text-xs/4",
    extra: "text-[13px]",
    content: "pb-3 text-[13px]/relaxed",
    tile: "size-7 rounded-[8px] [&_svg:not([class*='size-'])]:size-3.5",
    textMin: "min-h-7",
    line: "h-5",
    tileLine: "h-7",
    indicator: "size-6 [&_svg:not([class*='size-'])]:size-3.5",
  },
  md: {
    vars: "[--accordion-tile:36px] [--accordion-indicator:28px]",
    row: "min-h-14 py-3",
    title: "text-[15px]/[22px]",
    description: "text-[13px]/[18px]",
    extra: "text-sm",
    content: "pb-4 text-sm/relaxed",
    tile: "size-9 rounded-[10px] [&_svg:not([class*='size-'])]:size-4",
    textMin: "min-h-9",
    line: "h-[22px]",
    tileLine: "h-9",
    indicator: "size-7 [&_svg:not([class*='size-'])]:size-4",
  },
  lg: {
    vars: "[--accordion-tile:40px] [--accordion-indicator:32px]",
    row: "min-h-16 py-4",
    title: "text-base/6",
    description: "text-sm/5",
    extra: "text-[15px]",
    content: "pb-5 text-[15px]/relaxed",
    tile: "size-10 rounded-[12px] [&_svg:not([class*='size-'])]:size-[18px]",
    textMin: "min-h-10",
    line: "h-6",
    tileLine: "h-10",
    indicator: "size-8 [&_svg:not([class*='size-'])]:size-[18px]",
  },
};

// horizontal padding of rows and content: none for the flush ghost list, 12 / 16 / 20px inside containers,
// separated cards breathe a little more from sm up
const INSETS: Record<AccordionVariant, Record<AccordionSize, string>> = {
  ghost: {
    sm: "[--accordion-px:0px]",
    md: "[--accordion-px:0px]",
    lg: "[--accordion-px:0px]",
  },
  outline: {
    sm: "[--accordion-px:12px]",
    md: "[--accordion-px:16px]",
    lg: "[--accordion-px:20px]",
  },
  soft: {
    sm: "[--accordion-px:12px]",
    md: "[--accordion-px:16px]",
    lg: "[--accordion-px:20px]",
  },
  separated: {
    sm: "[--accordion-px:12px] sm:[--accordion-px:16px]",
    md: "[--accordion-px:16px] sm:[--accordion-px:20px]",
    lg: "[--accordion-px:20px] sm:[--accordion-px:24px]",
  },
};

const ROOT_VARIANTS: Record<AccordionVariant, string> = {
  ghost: "",
  outline: "border border-foreground/10",
  soft: "bg-foreground/5",
  separated: "gap-2",
};

// first and last items carry the container's corners, so hover tints and focus rings follow them
const ITEM_VARIANTS: Record<AccordionVariant, string> = {
  ghost: "",
  outline: "first:rounded-t-[inherit] last:rounded-b-[inherit]",
  soft: "first:rounded-t-[inherit] last:rounded-b-[inherit]",
  separated: "bg-surface text-surface-foreground shadow-raised",
};

// hairlines between items, drawn by the lower item so none sits above the first or below the last.
// soft insets them to the row padding, like a grouped list
const DIVIDER =
  "[&:not(:first-child)]:before:pointer-events-none [&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:top-0 [&:not(:first-child)]:before:h-px [&:not(:first-child)]:before:bg-foreground/10";
const DIVIDER_INSETS: Record<AccordionVariant, string> = {
  ghost: "[&:not(:first-child)]:before:inset-x-0",
  outline: "[&:not(:first-child)]:before:inset-x-0",
  soft: "[&:not(:first-child)]:before:inset-x-[var(--accordion-px)]",
  separated: "",
};

// the whole row is the target; touch-manipulation and the tap highlight live here so a copied accordion never
// flashes gray or waits for a double tap
const TRIGGER =
  "relative flex w-full cursor-pointer touch-manipulation select-none items-center px-[var(--accordion-px)] text-start outline-hidden [-webkit-tap-highlight-color:transparent] transition-[background-color,color] duration-fast ease-smooth focus-visible:outline-2 focus-visible:outline focus-visible:outline-ring aria-disabled:cursor-default data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

// ghost rows tint only the indicator on hover; rows in a container tint the whole row and keep its corners
const TRIGGER_VARIANTS: Record<"ghost" | "contained", string> = {
  ghost: "focus-visible:outline-offset-2",
  contained:
    "rounded-[inherit] hover:bg-foreground/5 active:bg-foreground/10 focus-visible:-outline-offset-2 group-data-[state=open]/accordion-item:rounded-b-none",
};

const ICON_TILE =
  "flex shrink-0 items-center justify-center bg-foreground/5 text-muted-foreground transition-colors duration-fast ease-smooth group-data-[state=open]/accordion-trigger:bg-[color:var(--tone-soft)] group-data-[state=open]/accordion-trigger:text-[color:var(--tone-soft-foreground)]";

// a round tile behind the chevron / plus: tinted with the tone while open, pressed in on tap
const INDICATOR =
  "flex shrink-0 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color,scale] duration-fast ease-smooth motion-safe:group-active/accordion-trigger:[scale:0.9] group-data-[state=open]/accordion-trigger:bg-[color:var(--tone-soft)] group-data-[state=open]/accordion-trigger:text-[color:var(--tone-soft-foreground)]";
const GHOST_INDICATOR = "group-hover/accordion-trigger:bg-foreground/5 group-active/accordion-trigger:bg-foreground/10";

const CONTENT_INNER =
  "ps-[calc(var(--accordion-px)+var(--accordion-lead)+var(--accordion-icon-indent))] pe-[var(--accordion-px)] text-muted-foreground";

// presets.collapse as variants (the same curves as collapsible): the height springs, the fade is a short tween so
// the answer is readable well before the height settles, and the exit is the preset's quick ease
const COLLAPSE: Variants = {
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      ...presets.collapse.transition,
      opacity: { duration: durations.normal, ease: easings.out },
    },
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: presets.collapse.exit.transition,
  },
};

// reduced motion: nothing slides. the answer fades in place, and fades out before the height snaps shut
const COLLAPSE_REDUCED: Variants = {
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0 },
      opacity: { duration: 0.15, ease: "linear" },
    },
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0, delay: 0.1 },
      opacity: { duration: 0.1, ease: "linear" },
    },
  },
};

const LAYOUT_TRANSITION = { layout: springs.smooth };

// the content clips only while it moves: hidden as soon as a close starts, visible once an open has settled, so
// focus rings and shadows of fields inside are never cut off at rest (the ghost variant has no inset at all)
function useClip(visible: boolean) {
  const overflow = useMotionValue<"hidden" | "visible">(visible ? "visible" : "hidden");
  return {
    overflow,
    onAnimationStart: (definition: unknown) => {
      if (definition === "closed") overflow.set("hidden");
    },
    onAnimationComplete: (definition: unknown) => {
      if (definition === "open") overflow.set("visible");
    },
  };
}

type AccordionContextValue = {
  open: string[];
  // opens one item (keeps the others in multiple mode): find in page uses it to reveal a kept-mounted answer
  reveal: (value: string) => void;
  variant: AccordionVariant;
  size: AccordionSize;
  radius: AccordionRadius;
  indicator: AccordionIndicator;
  indicatorPosition: AccordionIndicatorPosition;
  headingLevel: AccordionHeadingLevel;
  keepMounted: boolean;
  divided: boolean;
  reduced: boolean;
  classNames?: Partial<Record<AccordionSlot, string>>;
};

const AccordionContext = createContext<AccordionContextValue | null>(null);
const AccordionItemContext = createContext<{
  value: string;
  open: boolean;
} | null>(null);

function useAccordion() {
  const context = useContext(AccordionContext);
  if (!context) throw new Error("Accordion parts must be rendered inside <AccordionRoot>");
  return context;
}

function useAccordionItem() {
  const context = useContext(AccordionItemContext);
  if (!context) throw new Error("AccordionTrigger and AccordionContent must be rendered inside <AccordionItem>");
  return context;
}

// a cms string never crashes a render
function pick<T extends string | number>(value: T, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value) ? value : fallback;
}

function toList(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

// a string for single, a string[] for multiple (and string | string[] when the type is only known at runtime)
export type AccordionValue<T extends AccordionType = "single"> = T extends "multiple" ? string[] : string;

type AccordionBaseProps<T extends AccordionType> = Omit<ComponentProps<"div">, "defaultValue" | "dir" | "color"> & {
  // single: one item open at a time. multiple: any number
  type?: T;
  value?: AccordionValue<T>;
  defaultValue?: AccordionValue<T>;
  onValueChange?: (value: AccordionValue<T>) => void;
  // single type only: the open item can be closed again
  collapsible?: boolean;
  // ghost: flush with hairlines. outline / soft: one container. separated: every item its own card
  variant?: AccordionVariant;
  // row 44 / 56 / 64px with matching type, icon tile and indicator
  size?: AccordionSize;
  // corners of the outline / soft container and of separated cards
  radius?: AccordionRadius;
  indicator?: AccordionIndicator;
  indicatorPosition?: AccordionIndicatorPosition;
  // tints the open item's icon tile and indicator
  tone?: Tone;
  // any css color; replaces the tone
  color?: string;
  // the heading level wrapping each trigger, so the accordion fits the page outline
  headingLevel?: AccordionHeadingLevel;
  // disables every item
  disabled?: boolean;
  // keeps closed content in the DOM (collapsed, hidden until found): form state survives, search engines see the
  // answers, and the browser's find in page opens the row that holds the match
  keepMounted?: boolean;
  // hairlines between items (ignored by separated, whose cards have gaps)
  divided?: boolean;
  classNames?: Partial<Record<AccordionSlot, string>>;
};

export type AccordionRootProps<T extends AccordionType = "single"> = AccordionBaseProps<T>;

export function AccordionRoot<T extends AccordionType = "single">(accordionRootProps: AccordionRootProps<T>) {
  const {
    type = "single" as T,
    value,
    defaultValue,
    onValueChange,
    collapsible = true,
    variant = "ghost",
    size = "md",
    radius = "md",
    indicator = "chevron",
    indicatorPosition = "end",
    tone = "neutral",
    color,
    headingLevel = 3,
    disabled = false,
    keepMounted = false,
    divided = true,
    classNames,
    className,
    style,
    children,
    ...props
  } = useDefaults("accordion", accordionRootProps);
  const reduced = usePrefersReducedMotion();
  const multiple = type === "multiple";
  const safeVariant = pick(variant, ["ghost", "outline", "soft", "separated"], "ghost");
  const safeSize = pick(size, ["sm", "md", "lg"], "md");
  const safeRadius = pick(radius, ["none", "sm", "md", "lg", "full"], "md");
  const safeIndicator = pick(indicator, ["chevron", "plus", "none"], "chevron");
  const safePosition = pick(indicatorPosition, ["start", "end"], "end");
  const safeLevel = pick(Number(headingLevel) as AccordionHeadingLevel, [2, 3, 4, 5, 6], 3);

  // one list of open values for both types; the caller still sees a string for single and an array for multiple
  const controlled = useMemo(() => (value === undefined ? undefined : toList(value as string | string[])), [value]);
  const [open, setOpen] = useControllableState<string[]>({
    value: controlled,
    defaultValue: toList(defaultValue as string | string[] | undefined),
    onChange: (next) =>
      (onValueChange as ((next: string | string[]) => void) | undefined)?.(multiple ? next : (next[0] ?? "")),
  });

  const reveal = useCallback(
    (item: string) => setOpen((current) => (current.includes(item) ? current : multiple ? [...current, item] : [item])),
    [setOpen, multiple],
  );

  const context = useMemo<AccordionContextValue>(
    () => ({
      open,
      reveal,
      variant: safeVariant,
      size: safeSize,
      radius: safeRadius,
      indicator: safeIndicator,
      indicatorPosition: safePosition,
      headingLevel: safeLevel,
      keepMounted,
      divided,
      reduced,
      classNames,
    }),
    [
      open,
      reveal,
      safeVariant,
      safeSize,
      safeRadius,
      safeIndicator,
      safePosition,
      safeLevel,
      keepMounted,
      divided,
      reduced,
      classNames,
    ],
  );

  const shared = {
    "data-slot": "accordion",
    "data-variant": safeVariant,
    "data-size": safeSize,
    "data-tone": color ? "custom" : tone,
    disabled,
    className: cn(
      "flex w-full min-w-0 flex-col",
      toneClasses[tone] ?? toneClasses.neutral,
      SIZES[safeSize].vars,
      INSETS[safeVariant][safeSize],
      // the content lines up with the title, past a leading indicator
      safeIndicator !== "none" && safePosition === "start"
        ? "[--accordion-lead:calc(var(--accordion-indicator)+12px)]"
        : "[--accordion-lead:0px]",
      ROOT_VARIANTS[safeVariant],
      (safeVariant === "outline" || safeVariant === "soft") && SURFACE_RADII[safeRadius],
      className,
    ),
    style: { ...colorVars(color), ...style },
    ...props,
  };

  return (
    <AccordionContext.Provider value={context}>
      {multiple ? (
        <AccordionPrimitive.Root {...shared} type="multiple" value={open} onValueChange={setOpen}>
          {children}
        </AccordionPrimitive.Root>
      ) : (
        <AccordionPrimitive.Root
          {...shared}
          type="single"
          collapsible={collapsible}
          value={open[0] ?? ""}
          onValueChange={(next: string) => setOpen(next ? [next] : [])}
        >
          {children}
        </AccordionPrimitive.Root>
      )}
    </AccordionContext.Provider>
  );
}

export type AccordionItemProps = Omit<ComponentProps<"div">, "color"> & {
  value: string;
  disabled?: boolean;
};

export function AccordionItem({ value, disabled = false, className, children, ...props }: AccordionItemProps) {
  const { open, variant, radius, divided, reduced, classNames } = useAccordion();
  const isOpen = open.includes(value);
  const item = useMemo(() => ({ value, open: isOpen }), [value, isOpen]);

  return (
    <AccordionItemContext.Provider value={item}>
      <AccordionPrimitive.Item
        value={value}
        disabled={disabled}
        asChild
        className={cn(
          "group/accordion-item relative [--accordion-icon-indent:0px]",
          // an item whose trigger has an icon indents its content past the tile; phones keep the full width for it
          "sm:[&:has(>[data-slot=accordion-header]_[data-slot=accordion-icon])]:[--accordion-icon-indent:calc(var(--accordion-tile)+12px)]",
          ITEM_VARIANTS[variant],
          variant === "separated" && SURFACE_RADII[radius],
          divided && variant !== "separated" && [DIVIDER, DIVIDER_INSETS[variant]],
          classNames?.item,
          className,
        )}
        {...props}
      >
        {/* separated cards slide into place when items are added, removed or reordered */}
        <motion.div
          data-slot="accordion-item"
          layout={variant === "separated" && !reduced ? "position" : false}
          transition={LAYOUT_TRANSITION}
        >
          {children}
        </motion.div>
      </AccordionPrimitive.Item>
    </AccordionItemContext.Provider>
  );
}

function Indicator({ open, className }: { open: boolean; className?: string }) {
  const { indicator, indicatorPosition, size, variant, reduced } = useAccordion();
  const transition = reduced ? { duration: 0 } : springs.snappy;
  const start = indicatorPosition === "start";
  return (
    <span
      data-slot="accordion-indicator"
      aria-hidden
      className={cn(INDICATOR, SIZES[size].indicator, variant === "ghost" && GHOST_INDICATOR, className)}
    >
      {indicator === "plus" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="M5 12h14" />
          {/* the vertical bar turns flat and fades, leaving a minus */}
          <motion.path
            d="M12 5v14"
            initial={false}
            animate={{ rotate: open ? 90 : 0, opacity: open ? 0 : 1 }}
            transition={transition}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          />
        </svg>
      ) : (
        // a leading chevron points at the title and turns down, like a disclosure triangle; a trailing one flips
        <motion.span
          className={cn("flex", start && "rtl:-scale-x-100")}
          initial={false}
          animate={{ rotate: open ? (start ? 90 : 180) : 0 }}
          transition={transition}
        >
          {start ? <ChevronRight /> : <ChevronDown />}
        </motion.span>
      )}
    </span>
  );
}

export type AccordionTriggerProps = Omit<ComponentProps<"button">, "color"> & {
  // leading icon in a tinted tile; the content indents to the title
  icon?: ReactNode;
  // a second line under the title
  description?: ReactNode;
  // a short value before the indicator: a price, a count, a status
  extra?: ReactNode;
};

export function AccordionTrigger({ icon, description, extra, className, children, ...props }: AccordionTriggerProps) {
  const { variant, size, radius, indicator, indicatorPosition, headingLevel, classNames } = useAccordion();
  const { open } = useAccordionItem();
  const s = SIZES[size];
  const Heading = `h${headingLevel}` as "h3";
  // the height of the first row, so the indicator and the extra value stay on it when the title wraps
  const firstRow = icon ? s.tileLine : s.line;
  const mark =
    indicator === "none" ? null : (
      <span className={cn("flex shrink-0 items-center", firstRow)}>
        <Indicator open={open} className={classNames?.indicator} />
      </span>
    );

  return (
    <AccordionPrimitive.Header asChild>
      <Heading data-slot="accordion-header" className="flex rounded-[inherit]">
        <AccordionPrimitive.Trigger
          data-slot="accordion-trigger"
          className={cn(
            "group/accordion-trigger",
            TRIGGER,
            s.row,
            variant === "ghost" ? [TRIGGER_VARIANTS.ghost, RADII[radius][size]] : TRIGGER_VARIANTS.contained,
            classNames?.trigger,
            className,
          )}
          {...(props as ComponentProps<typeof AccordionPrimitive.Trigger>)}
        >
          <span className="flex min-w-0 flex-1 items-start gap-3">
            {indicatorPosition === "start" && mark}
            {icon && (
              <span data-slot="accordion-icon" aria-hidden className={cn(ICON_TILE, s.tile, classNames?.icon)}>
                {icon}
              </span>
            )}
            {/* the extra value drops under the title once the title would get narrower than 8rem, so a price never
                squeezes it into a word per line in a narrow card */}
            <span className="flex min-w-0 flex-1 flex-wrap items-start gap-x-3">
              <span className={cn("flex min-w-0 grow basis-32 flex-col justify-center gap-0.5", icon && s.textMin)}>
                <span
                  data-slot="accordion-title"
                  className={cn("font-medium text-pretty [overflow-wrap:break-word] text-foreground", s.title, classNames?.title)}
                >
                  {children}
                </span>
                {description && (
                  <span
                    data-slot="accordion-description"
                    className={cn(
                      "text-pretty [overflow-wrap:break-word] text-muted-foreground",
                      s.description,
                      classNames?.description,
                    )}
                  >
                    {description}
                  </span>
                )}
              </span>
              {extra && (
                <span
                  data-slot="accordion-extra"
                  className={cn(
                    "flex shrink-0 items-center text-muted-foreground tabular-nums",
                    firstRow,
                    s.extra,
                    classNames?.extra,
                  )}
                >
                  {extra}
                </span>
              )}
            </span>
            {indicatorPosition === "end" && mark}
          </span>
        </AccordionPrimitive.Trigger>
      </Heading>
    </AccordionPrimitive.Header>
  );
}

export type AccordionContentProps = ComponentProps<"div"> & {
  // overrides the root's keepMounted for this row
  keepMounted?: boolean;
};

// the height animates on a wrapper that clips while it moves; padding lives on the inner div so it never squashes.
// className and props go to the inner div
export function AccordionContent({
  keepMounted: keepMountedProp,
  className,
  children,
  ...props
}: AccordionContentProps) {
  const { size, keepMounted: keepMountedRoot, reduced, classNames } = useAccordion();
  const { open } = useAccordionItem();
  const variants = reduced ? COLLAPSE_REDUCED : COLLAPSE;

  const inner = (
    <div
      data-slot="accordion-content-inner"
      className={cn(CONTENT_INNER, SIZES[size].content, classNames?.content, className)}
      {...props}
    >
      {children}
    </div>
  );

  if (keepMountedProp ?? keepMountedRoot) return <KeptContent variants={variants}>{inner}</KeptContent>;

  // radix would unmount closed content at once; forceMount hands presence to AnimatePresence so the exit can run
  return (
    <AnimatePresence initial={false}>
      {open && (
        <PresentContent key="content" variants={variants}>
          {inner}
        </PresentContent>
      )}
    </AnimatePresence>
  );
}

function PresentContent({ variants, children }: { variants: Variants; children: ReactNode }) {
  // present on the first render (defaultValue) means no entrance, so nothing to clip
  const clip = useClip(useContext(PresenceContext)?.initial === false);
  return (
    <AccordionPrimitive.Content forceMount asChild>
      <motion.div
        data-slot="accordion-content"
        variants={variants}
        initial="closed"
        animate="open"
        exit="closed"
        onAnimationStart={clip.onAnimationStart}
        onAnimationComplete={clip.onAnimationComplete}
        style={{ overflow: clip.overflow }}
      >
        {children}
      </motion.div>
    </AccordionPrimitive.Content>
  );
}

// always in the dom. while closing it is inert; once shut it becomes hidden="until-found", so the browser's find in
// page still reaches the answer and opens its row when it does (beforematch)
function KeptContent({ variants, children }: { variants: Variants; children: ReactNode }) {
  const { reveal } = useAccordion();
  const { value, open } = useAccordionItem();
  const ref = useRef<HTMLDivElement>(null);
  const clip = useClip(open);
  const [settled, setSettled] = useState(!open);
  const [previous, setPrevious] = useState(open);
  if (previous !== open) {
    setPrevious(open);
    setSettled(false);
  }

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (settled) node.setAttribute("hidden", "until-found");
    else node.removeAttribute("hidden");
  }, [settled]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onMatch = () => reveal(value);
    node.addEventListener("beforematch", onMatch);
    return () => node.removeEventListener("beforematch", onMatch);
  }, [reveal, value]);

  return (
    <AccordionPrimitive.Content forceMount asChild>
      <motion.div
        ref={ref}
        data-slot="accordion-content"
        variants={variants}
        initial={false}
        animate={open ? "open" : "closed"}
        {...inertProps(!open && !settled)}
        onAnimationStart={clip.onAnimationStart}
        onAnimationComplete={(definition) => {
          clip.onAnimationComplete(definition);
          if (definition === "closed") setSettled(true);
        }}
        style={{ overflow: clip.overflow }}
      >
        {children}
      </motion.div>
    </AccordionPrimitive.Content>
  );
}

export type AccordionProps<T extends AccordionType = "single"> = Omit<AccordionBaseProps<T>, "children"> & {
  // { value, title, description?, icon?, extra?, content, disabled? }; values must be unique
  items: AccordionItemData[];
};

export function Accordion<T extends AccordionType = "single">(accordionProps: AccordionProps<T>) {
  const {
    items,
    type = "single" as T,
    value,
    defaultValue,
    onValueChange,
    collapsible = true,
    variant = "ghost",
    size = "md",
    radius = "md",
    indicator = "chevron",
    indicatorPosition = "end",
    tone = "neutral",
    color,
    headingLevel = 3,
    disabled = false,
    keepMounted = false,
    divided = true,
    classNames,
    ...props
  } = useDefaults("accordion", accordionProps);
  // duplicate values would open together; the last one wins
  const unique = useMemo(() => Array.from(new Map((items ?? []).map((item) => [item.value, item])).values()), [items]);
  if (!unique.length) return null;

  return (
    <AccordionRoot<T>
      type={type}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      collapsible={collapsible}
      variant={variant}
      size={size}
      radius={radius}
      indicator={indicator}
      indicatorPosition={indicatorPosition}
      tone={tone}
      color={color}
      headingLevel={headingLevel}
      disabled={disabled}
      keepMounted={keepMounted}
      divided={divided}
      classNames={classNames}
      {...props}
    >
      {unique.map((item) => (
        <AccordionItem key={item.value} value={item.value} disabled={item.disabled}>
          <AccordionTrigger icon={item.icon} description={item.description} extra={item.extra}>
            {item.title}
          </AccordionTrigger>
          <AccordionContent>{item.content}</AccordionContent>
        </AccordionItem>
      ))}
    </AccordionRoot>
  );
}
